import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { TenantService } from '@/cloud/services/tenant.service';
import { LogsBlock } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';
import { parseUtc } from '@/cloud/models/device-status';

/** A short log excerpt, coloured by severity. Text is shown as text — never as HTML. */
@Component({
    selector: 'app-assistant-log-excerpt',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <details class="log-block" open>
            <summary>
                <i class="pi pi-list" aria-hidden="true"></i>
                <span>{{ block().title || block().deviceName }}</span>
                <span class="muted">· {{ block().entries.length }} lines</span>
            </summary>
            <ol class="log-lines">
                @for (e of block().entries; track $index) {
                    <li [attr.data-level]="level(e.level)">
                        <span class="log-time">{{ time(e.timestampUtc) }}</span>
                        <span class="log-level">{{ e.level }}</span>
                        <span class="log-msg">{{ e.message }}</span>
                    </li>
                }
            </ol>
            <button type="button" class="quick" (click)="openLogs()"><i class="pi pi-external-link" aria-hidden="true"></i>{{ t()('assistant.logs.open') }}</button>
        </details>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantLogExcerptComponent {
    readonly block = input.required<LogsBlock>();
    private router = inject(Router);
    private tenants = inject(TenantService);
    readonly t = inject(TranslationService).t;

    level(level: string): string {
        const l = (level || '').toLowerCase();
        if (l.startsWith('crit') || l.startsWith('fatal')) return 'critical';
        if (l.startsWith('err')) return 'error';
        if (l.startsWith('warn')) return 'warning';
        return 'info';
    }

    time(value: string | null): string {
        const t = parseUtc(value);
        return t === null ? '' : new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }

    openLogs(): void {
        const b = this.block();
        if (b.namespaceId && b.namespaceId !== this.tenants.selectedTenantId()) this.tenants.select(b.namespaceId);
        this.router.navigate(['/device', b.deviceId, 'logs']);
    }
}
